import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { IParagraphNode } from "~/models/IParagraphNode";
import type { IPosition } from "~/models/IPosition";
import type { IParagraphEntry, IUserEntry } from "~/models/IUserEntry";

export function useNodesWithEntries(
  legalTextParagraph: Ref<ILegalTextParagraph>,
  entries: Ref<IParagraphEntry[]>
) {
  const flattenedLeaves = computed(() => {
    const traverse = (node: IParagraphNode) => {
      if (node.children.length === 0) {
        return [node];
      }
      return node.children.flatMap((child) => traverse(child));
    };
    return legalTextParagraph.value.nodes.flatMap((node) => traverse(node));
  });

  const findStartAndEndInLeaves = (position?: IPosition) => {
    const start = flattenedLeaves.value.findIndex(
      (leaf) => leaf.id === position?.node_start_id
    );
    const end = flattenedLeaves.value.findIndex(
      (leaf) => leaf.id === position?.node_end_id
    );
    return { start, end };
  };

  const entryMap = computed(() => {
    const leaves: (IParagraphNode & {
      entries: IUserEntry[];
    })[] = flattenedLeaves.value.map((leaf) => ({ ...leaf, entries: [] }));
    for (const entry of entries.value) {
      const { start, end } = findStartAndEndInLeaves(entry.position);
      for (let i = start; i <= end; i++) {
        leaves[i]?.entries.push(entry);
      }
    }
    const hashMap = leaves.reduce(
      (acc, leaf) => Object.assign(acc, { [leaf.id]: leaf.entries }),
      {} as { [id: number]: IUserEntry[] }
    );
    return hashMap;
  });

  const nodesWithEntries = computed(() => {
    const traverse = (node: IParagraphNode) => {
      if (node.children.length === 0) {
        return { ...node, entries: entryMap.value[node.id] ?? [] };
      }
      return {
        ...node,
        children: node.children.map((child) => traverse(child)),
      };
    };
    return legalTextParagraph.value.nodes.map((node) => traverse(node));
  });

  return {
    nodesWithEntries,
  };
}
